import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ImageIcon, Loader2, Handshake } from "lucide-react";

import { listMunicipalityCollaborators } from "@/api/municipality_collaborators";
import type { ResidentSummary } from "@/api/residents";
import type { TrustComplaint, TrustComplaintPayload, TrustComplaintStatus } from "@/api/trust_complaints";
import { TRUST_COMPLAINT_STATUS_ORDER } from "@/api/trust_complaints";
import { ComplaintActivity } from "@/components/trust_suite/ComplaintActivity";
import { ComplaintContactCard } from "@/components/trust_suite/ComplaintContactCard";
import { ResidentLinkCard } from "@/components/trust_suite/ResidentLinkCard";
import { residentCreateDefaultsFromComplaint } from "@/components/trust_suite/residentLinkDefaults";
import { ComplaintDocuments } from "@/components/trust_suite/ComplaintDocuments";
import { ComplaintTasks } from "@/components/trust_suite/ComplaintTasks";
import { DetailNotesCard } from "@/components/workflow_tracking/DetailNotesCard";
import {
  trustComplaintDetailTitle,
  trustComplaintSourceLabel,
  trustComplaintStatusLabel,
  trustComplaintStatusPillClass,
} from "@/components/trust_suite/trustSuiteDisplay";
import { formatRelativeTime } from "@/components/module_summary/formatRelativeTime";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

type ComplaintDetailProps = {
  municipalityId: number;
  fileViewerEnabled: boolean;
  complaint: TrustComplaint | null;
  loading?: boolean;
  updating?: boolean;
  onUpdate: (patch: TrustComplaintPayload) => void;
  onViewResident?: (residentId: number) => void;
};

const UNASSIGNED = "unassigned";

export function ComplaintDetail({
  municipalityId,
  fileViewerEnabled,
  complaint,
  loading = false,
  updating = false,
  onUpdate,
  onViewResident,
}: ComplaintDetailProps) {
  const [notesDraft, setNotesDraft] = useState(complaint?.internal_notes ?? "");
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    setNotesDraft(complaint?.internal_notes ?? "");
  }, [complaint?.id, complaint?.internal_notes]);

  useEffect(() => {
    setActiveTab("overview");
  }, [complaint?.id]);

  const { data: collaborators = [] } = useQuery({
    queryKey: ["municipality_collaborators", municipalityId],
    queryFn: () => listMunicipalityCollaborators(municipalityId),
    enabled: complaint != null && activeTab === "overview",
  });

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading concern…
      </div>
    );
  }

  if (!complaint) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center text-muted-foreground">
        <Handshake className="h-10 w-10 opacity-30" />
        <p className="text-sm">Select a concern to view details</p>
      </div>
    );
  }

  const notesDirty = notesDraft.trim() !== (complaint.internal_notes ?? "").trim();

  const saveNotes = () => {
    const next = notesDraft.trim();
    if (next !== (complaint.internal_notes ?? "").trim()) {
      onUpdate({ internal_notes: next || null });
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="trust-complaint-detail">
      <div className="shrink-0 border-b bg-background px-4 py-4 sm:px-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={cn(
                  "inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium",
                  trustComplaintStatusPillClass(complaint.status),
                )}
              >
                {trustComplaintStatusLabel(complaint.status)}
              </span>
              <Badge variant="secondary" className="font-normal">
                {trustComplaintSourceLabel(complaint.source)}
              </Badge>
              {complaint.photo_count > 0 ? (
                <Badge variant="outline" className="h-5 gap-1 px-1.5 text-[10px] font-normal">
                  <ImageIcon className="h-3 w-3" />
                  {complaint.photo_count}
                </Badge>
              ) : null}
            </div>
            <h2 className="text-lg font-semibold leading-snug tracking-tight sm:text-xl">
              {trustComplaintDetailTitle(complaint)}
            </h2>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <span>#{complaint.id}</span>
              <span>Submitted {formatRelativeTime(complaint.submitted_at)}</span>
              <span>Updated {formatRelativeTime(complaint.updated_at)}</span>
              <span>Assignee: {complaint.assigned_to_name ?? "Unassigned"}</span>
            </div>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex min-h-0 flex-1 flex-col">
        <div className="shrink-0 border-b px-4 py-2 sm:px-6">
          <TabsList className="h-9 w-full justify-start sm:w-auto">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            {fileViewerEnabled ? (
              <TabsTrigger value="files">Files</TabsTrigger>
            ) : null}
            <TabsTrigger value="activity">Activity</TabsTrigger>
          </TabsList>
        </div>

        <ScrollArea className="min-h-0 flex-1" viewportClassName="min-w-0">
          <TabsContent value="overview" className="m-0 px-4 py-4 sm:px-6">
            <div className="grid gap-4 lg:grid-cols-2">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Status</CardTitle>
                  <CardDescription>
                    Set where this concern sits in your team's workflow. Any status can move to any other.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="trust-detail-status">Status</Label>
                    <Select
                      value={complaint.status}
                      onValueChange={(value) => onUpdate({ status: value as TrustComplaintStatus })}
                      disabled={updating}
                    >
                      <SelectTrigger id="trust-detail-status">
                        <SelectValue placeholder="Set status" />
                      </SelectTrigger>
                      <SelectContent>
                        {TRUST_COMPLAINT_STATUS_ORDER.map((status) => (
                          <SelectItem key={status} value={status}>
                            {trustComplaintStatusLabel(status)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <Label htmlFor="trust-detail-assignee">Assignee</Label>
                    <Select
                      value={complaint.assigned_to_id ? String(complaint.assigned_to_id) : UNASSIGNED}
                      onValueChange={(value) =>
                        onUpdate({
                          assigned_to_id: value === UNASSIGNED ? null : Number(value),
                        })
                      }
                      disabled={updating}
                    >
                      <SelectTrigger id="trust-detail-assignee">
                        <SelectValue placeholder="Unassigned" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                        {collaborators.map((collaborator) => (
                          <SelectItem key={collaborator.id} value={String(collaborator.id)}>
                            {collaborator.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-4">
                <DetailNotesCard
                  id="trust-internal-notes"
                  value={notesDraft}
                  onChange={setNotesDraft}
                  onSave={saveNotes}
                  dirty={notesDirty}
                  saving={updating}
                  placeholder="Follow-up steps, department handoffs, context for the next shift…"
                />

                <Card>
                  <CardContent className="pt-6">
                    <ComplaintTasks municipalityId={municipalityId} complaintId={complaint.id} />
                  </CardContent>
                </Card>

                <ComplaintContactCard complaint={complaint} />

                <ResidentLinkCard
                  municipalityId={municipalityId}
                  residentId={complaint.resident_id}
                  resident={complaint.resident as ResidentSummary | null | undefined}
                  updating={updating}
                  onLink={(residentId) => onUpdate({ resident_id: residentId })}
                  onViewResident={onViewResident}
                  createDefaults={residentCreateDefaultsFromComplaint(complaint)}
                  createDialogTitle="Create resident from concern"
                  createDialogDescription="Creates a profile from this concern's contact info and links it to this concern."
                />
              </div>
            </div>

            <div className="mt-4 space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Concern details</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="whitespace-pre-wrap text-sm leading-relaxed">{complaint.complaint}</p>
                </CardContent>
              </Card>

              {complaint.photos.length > 0 ? (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Photos</CardTitle>
                    <CardDescription>
                      {complaint.photo_count} attached{" "}
                      {complaint.photo_count === 1 ? "image" : "images"}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                      {complaint.photos.map((photo) => (
                        <a
                          key={photo.id}
                          href={photo.url}
                          target="_blank"
                          rel="noreferrer"
                          className="group overflow-hidden rounded-lg border bg-background transition-colors hover:border-primary/40"
                        >
                          <div className="aspect-[4/3] bg-muted/30">
                            <img
                              src={photo.url}
                              alt={photo.filename}
                              className="h-full w-full object-cover"
                              loading="lazy"
                            />
                          </div>
                          <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
                            <ImageIcon className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate">{photo.filename}</span>
                          </div>
                        </a>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ) : null}
            </div>
          </TabsContent>

          {fileViewerEnabled ? (
            <TabsContent value="files" className="m-0 min-w-0 px-4 py-4 sm:px-6">
              <ComplaintDocuments
                municipalityId={municipalityId}
                complaintId={complaint.id}
                fileViewerEnabled={fileViewerEnabled}
                embedded
              />
            </TabsContent>
          ) : null}

          <TabsContent value="activity" className="m-0 px-4 py-4 sm:px-6">
            <ComplaintActivity
              municipalityId={municipalityId}
              complaintId={complaint.id}
              fetchEnabled={activeTab === "activity"}
            />
          </TabsContent>
        </ScrollArea>
      </Tabs>
    </div>
  );
}
